import { formatNumber } from './fieldFormat.js';
import { activeExclusiveOption, type FieldValue, type FieldValues } from './fieldValues.js';
import type { FieldOptions } from './useFieldOptions.js';
import type { PresetOption, WorkflowField, WorkflowStep } from './workflows.types.js';

const MAX_SHOWN_LABELS = 2;
export const MAX_TEXT_CHARS = 50;

export interface SummaryPart {
  text: string;
  tooltip: string | null;
}

export interface PreRunSummary {
  title: string;
  parts: SummaryPart[];
}

function isSet(value: FieldValue | undefined): boolean {
  if (value === undefined || value === null) return false;
  return Array.isArray(value) ? value.length > 0 : value !== '';
}

function labelsOf(ids: string[], field: WorkflowField, optionsFor: FieldOptions): string[] {
  const { options, more = [] } = optionsFor(field);
  const all = [...options, ...more];
  return ids.map((id) => all.find((option) => option.id === id)?.label ?? id);
}

function part(label: string, labels: string[]): SummaryPart {
  if (labels.length <= MAX_SHOWN_LABELS) {
    return { text: `${label} ${labels.join(', ')}`, tooltip: null };
  }
  return {
    text: `${label} ${labels.slice(0, MAX_SHOWN_LABELS).join(', ')} …`,
    tooltip: labels.join(', '),
  };
}

function textPart(label: string, raw: string): SummaryPart {
  const text = raw.replace(/\s+/g, ' ').trim();
  if (text.length <= MAX_TEXT_CHARS) return { text: `${label} ${text}`, tooltip: null };
  return { text: `${label} ${text.slice(0, MAX_TEXT_CHARS)}…`, tooltip: text };
}

function summarise(
  field: WorkflowField,
  values: FieldValues,
  optionsFor: FieldOptions,
): SummaryPart[] {
  if (field.kind === 'collapsible') {
    return field.fields.flatMap((nested) => summarise(nested, values, optionsFor));
  }
  if (field.kind === 'presetCards') return [];
  // The group's own value is the active option's key, not a number to show.
  if (field.kind === 'exclusiveGroup') {
    const active = activeExclusiveOption(field, values);
    return active === undefined ? [] : summarise(active, values, optionsFor);
  }

  const value = values[field.key];
  if (!isSet(value)) return [];

  if (field.kind === 'number') {
    return [{ text: `${field.label} ${formatNumber(field, Number(value))}`, tooltip: null }];
  }
  if (field.kind === 'multiselect') {
    const ids = Array.isArray(value) ? value.map(String) : [];
    return [part(field.label, labelsOf(ids, field, optionsFor))];
  }
  if (field.kind === 'upload') {
    return [part(field.label, Array.isArray(value) ? value.map(String) : [String(value)])];
  }
  if (field.kind === 'select') {
    return [part(field.label, labelsOf([String(value)], field, optionsFor))];
  }
  return [textPart(field.label, String(value))];
}

function selectedPreset(step: WorkflowStep, values: FieldValues): PresetOption | undefined {
  for (const field of step.fields) {
    if (field.kind !== 'presetCards') continue;
    return field.options.find((option) => option.id === values[field.key]);
  }
  return undefined;
}

export function presetFieldLabel(step: WorkflowStep): string | null {
  return step.fields.find((field) => field.kind === 'presetCards')?.label ?? null;
}

export function presetLabelOf(step: WorkflowStep, values: FieldValues): string | null {
  return selectedPreset(step, values)?.label ?? null;
}

export function stepSummaryLine(step: WorkflowStep, values: FieldValues): string {
  for (const field of step.fields) {
    if (field.kind !== 'presetCards') continue;
    const selected = field.options.find((option) => option.id === values[field.key]);
    if (selected !== undefined) return `${field.label} · ${selected.label}`;
  }

  return step.summaryLabel ?? step.title;
}

export function preRunSummary(
  step: WorkflowStep,
  values: FieldValues,
  optionsFor: FieldOptions,
): PreRunSummary {
  const preset = selectedPreset(step, values);

  return {
    title:
      preset === undefined ? `About to run ${step.title}` : `About to run ${preset.label} preset`,
    parts: [
      ...(preset?.chips ?? []).map((chip) => ({
        text: `${chip.label} ${chip.text}`,
        tooltip: null,
      })),
      ...step.fields.flatMap((field) => summarise(field, values, optionsFor)),
    ],
  };
}
